import React, {
    RefCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"

import { IAnimateRef, IDelayedAnimateRef } from "../../type"

type TScene = React.ForwardRefExoticComponent<
    React.RefAttributes<IAnimateRef> & { onExit: () => void }
>

const SceneWrapper = React.forwardRef<
    IDelayedAnimateRef,
    { scene: TScene; onExit: () => void }
>(({ scene: SceneComponent, onExit }, ref) => {
    const [refOkCb, setRefOkCb] = useState<
        RefCallback<IAnimateRef> | undefined
    >()
    const innerRef = useRef<IAnimateRef | null>(null)
    const children = useMemo(
        () =>
            refOkCb && (
                <SceneComponent
                    onExit={onExit}
                    ref={(ele) => {
                        // console.log("first:", refOkCb)
                        refOkCb?.(ele)
                    }}
                />
            ),
        [SceneComponent, refOkCb]
    )

    useImperativeHandle(
        ref,
        () => ({
            fadeIn: (delay, immediate) =>
                new Promise((res, rej) => {
                    let init = false
                    setTimeout(() => {
                        setRefOkCb(() => (ele: IAnimateRef) => {
                            if (ele) {
                                innerRef.current = ele
                                if (!init) {
                                    ele.fadeIn(immediate).then(res).catch(rej)
                                    init = true
                                }
                            } else {
                                innerRef.current = null
                            }
                        })
                    }, delay)
                }),
            fadeOut: (delay, immediate) =>
                new Promise((res, rej) => {
                    setTimeout(async () => {
                        if (innerRef.current) {
                            await innerRef.current.fadeOut(immediate)
                            setRefOkCb(undefined)
                            res()
                        } else {
                            rej(
                                "[SceneWrapper] innerRef.current is empty when fadeOut, which should not happen!"
                            )
                        }
                    }, delay)
                }),
        }),
        []
    )
    return children
})
SceneWrapper.displayName = "SceneWrapper"

export default SceneWrapper
